// Server-emitted "knowledge" / "flash" events. The server stays in the
// abstract domain (codes + payloads); each client renders to text in the
// player's chosen language. This is what lets a user toggle RU↔UK mid-game
// without their existing knowledge log going stale.
//
// Adding a new event:
//   1. Extend `KnowledgeEvent` below with a new discriminated case.
//   2. Add RU and UK rendering arms in `renderKnowledge`.
//   3. Construct it from server code (see `room.ts`).

import type { ActionVerb, Faction, Lang, RoleConfig } from './types.js';

export type KnowledgeEvent =
  | { code: 'executioner-target'; targetName: string }
  | { code: 'executioner-converted-to-jester' }
  | { code: 'mayor-revealed'; mayorName: string }
  | { code: 'priest-reveal'; targetName: string; roleId: string }
  | { code: 'sheriff-check'; targetName: string; black: boolean }
  | { code: 'don-check'; targetName: string; isSheriff: boolean }
  | { code: 'consigliere-inspect'; targetName: string; roleId: string }
  | { code: 'investigator-result'; targetName: string; isKiller: boolean }
  | { code: 'watcher-visitors'; targetName: string; visitorNames: string[] }
  | { code: 'tracker-visited'; targetName: string; visitedNames: string[] }
  | {
      code: 'spy-tail';
      targetName: string;
      incoming: { actorName: string; verb: ActionVerb }[];
      outgoing: { verb: ActionVerb; targetName: string }[];
    }
  | { code: 'doctor-self-save-limit' }
  | { code: 'spy-mafia-target'; targetName: string }
  | { code: 'spy-don-check'; targetName: string }
  | { code: 'cult-recruit'; targetName: string }      // cultist learns who joined
  | { code: 'cult-recruited' }                          // target learns they're in the cult
  | { code: 'witch-control'; targetName: string; ontoName: string }; // witch learns the puppeteering

// Verb name in each language. Used by spy-tail rendering. Keep in sync with
// `VERB_COPY` on the client; this table is the canonical translation source.
// Exported so the host night dashboard and the Codex render localized verb
// labels instead of leaking the raw engine keys ('kill', 'check', …).
export const VERB_NAME: Record<ActionVerb, Record<Lang, string>> = {
  kill: { ru: 'убийство', uk: 'убивство' },
  save: { ru: 'спасение', uk: 'порятунок' },
  check: { ru: 'проверка', uk: 'перевірка' },
  distract: { ru: 'отвлечение', uk: 'відволікання' },
  douse: { ru: 'обливание', uk: 'обливання' },
  ignite: { ru: 'поджог', uk: 'підпал' },
  shield: { ru: 'щит', uk: 'щит' },
  protect: { ru: 'защита', uk: 'захист' },
  redirect: { ru: 'подмена', uk: 'підміна' },
  alert: { ru: 'тревога', uk: 'тривога' },
  frame: { ru: 'подстава', uk: 'підстава' },
  vest: { ru: 'жилет', uk: 'жилет' },
  silence: { ru: 'молчание', uk: 'мовчання' },
  watch: { ru: 'слежка', uk: 'стеження' },
  inspect: { ru: 'опознание', uk: 'розпізнання' },
  execute: { ru: 'казнь', uk: 'страта' },
  investigate: { ru: 'расследование', uk: 'розслідування' },
  reveal: { ru: 'раскрытие', uk: 'розкриття' },
  spy: { ru: 'хвост', uk: 'хвіст' },
  jail: { ru: 'арест', uk: 'арешт' },
  track: { ru: 'трекинг', uk: 'трекінг' },
  taunt: { ru: 'провокация', uk: 'провокація' },
  plague: { ru: 'заражение', uk: 'зараження' },
  rampage: { ru: 'резня', uk: 'різанина' },
  convert: { ru: 'вербовка', uk: 'вербування' },
  control: { ru: 'контроль', uk: 'контроль' },
};

// Canonical side label per faction. Exported as the single source of truth so
// RoleCard, Codex, RoleReveal and the goal layer stop re-defining (and drifting
// on) their own copies.
export const FACTION_LABEL: Record<Faction, Record<Lang, string>> = {
  town: { ru: 'МИРНЫЕ', uk: 'МИРНІ' },
  mafia: { ru: 'МАФИЯ', uk: 'МАФІЯ' },
  solo: { ru: 'ОДИНОЧКА', uk: 'ОДИНАК' },
};

/**
 * Render a `KnowledgeEvent` to a human-readable string in the requested
 * language. `roleById` is needed for events that name a role (priest reveal,
 * consigliere inspect) — the role's title string lives on the role config
 * and is therefore i18n-aware out of the box.
 */
export function renderKnowledge(
  ev: KnowledgeEvent,
  lang: Lang,
  roleById: Record<string, RoleConfig>,
): string {
  switch (ev.code) {
    case 'executioner-target':
      return lang === 'ru'
        ? `Ваша цель — ${ev.targetName}. Добейтесь, чтобы её повесили.`
        : `Ваша ціль — ${ev.targetName}. Доможіться, щоб її повісили.`;
    case 'executioner-converted-to-jester':
      return lang === 'ru'
        ? 'Ваша цель убита. Вы становитесь Шутом — побеждайте, если вас повесят.'
        : 'Вашу ціль убито. Ви стаєте Блазнем — перемагайте, якщо вас повісять.';
    case 'mayor-revealed':
      return lang === 'ru'
        ? `Игрок ${ev.mayorName} объявляет себя МЭРОМ. Его голос теперь считается за два.`
        : `Гравець ${ev.mayorName} оголошує себе МЕРОМ. Його голос тепер рахується як два.`;
    case 'priest-reveal': {
      const role = roleById[ev.roleId];
      const title = role?.title[lang] ?? ev.roleId;
      const faction = role ? FACTION_LABEL[role.faction][lang] : '';
      return `${ev.targetName} — ${title} (${faction})`;
    }
    case 'sheriff-check':
      return ev.black
        ? lang === 'ru'
          ? `Игрок ${ev.targetName} — ЧЁРНАЯ карта`
          : `Гравець ${ev.targetName} — ЧОРНА карта`
        : lang === 'ru'
          ? `Игрок ${ev.targetName} — красная карта`
          : `Гравець ${ev.targetName} — червона карта`;
    case 'don-check':
      return ev.isSheriff
        ? lang === 'ru'
          ? `Игрок ${ev.targetName} — ШЕРИФ`
          : `Гравець ${ev.targetName} — ШЕРИФ`
        : lang === 'ru'
          ? `Игрок ${ev.targetName} — не шериф`
          : `Гравець ${ev.targetName} — не шериф`;
    case 'consigliere-inspect': {
      const role = roleById[ev.roleId];
      const title = (role?.title[lang] ?? ev.roleId).toUpperCase();
      const faction = role ? FACTION_LABEL[role.faction][lang] : '';
      return lang === 'ru'
        ? `Игрок ${ev.targetName} — ${title} (${faction})`
        : `Гравець ${ev.targetName} — ${title} (${faction})`;
    }
    case 'investigator-result':
      return ev.isKiller
        ? lang === 'ru'
          ? `Игрок ${ev.targetName} способен убивать.`
          : `Гравець ${ev.targetName} здатний убивати.`
        : lang === 'ru'
          ? `Игрок ${ev.targetName} не способен убивать.`
          : `Гравець ${ev.targetName} не здатний убивати.`;
    case 'watcher-visitors':
      if (!ev.visitorNames.length) {
        return lang === 'ru'
          ? `К ${ev.targetName} никто не приходил.`
          : `До ${ev.targetName} ніхто не приходив.`;
      }
      return lang === 'ru'
        ? `К ${ev.targetName} приходили: ${ev.visitorNames.join(', ')}`
        : `До ${ev.targetName} приходили: ${ev.visitorNames.join(', ')}`;
    case 'tracker-visited':
      if (!ev.visitedNames.length) {
        return lang === 'ru'
          ? `${ev.targetName} никуда не ходил.`
          : `${ev.targetName} нікуди не ходив.`;
      }
      return lang === 'ru'
        ? `${ev.targetName} ходил к: ${ev.visitedNames.join(', ')}`
        : `${ev.targetName} ходив до: ${ev.visitedNames.join(', ')}`;
    case 'spy-tail': {
      const inc =
        ev.incoming
          .map((i) => `${i.actorName} (${VERB_NAME[i.verb][lang]})`)
          .join(', ') || '—';
      const out =
        ev.outgoing
          .map((o) => `${VERB_NAME[o.verb][lang]} → ${o.targetName}`)
          .join(', ') || '—';
      return lang === 'ru'
        ? `Хвост за ${ev.targetName}: пришли — ${inc}; ушёл — ${out}`
        : `Хвіст за ${ev.targetName}: приходили — ${inc}; пішов — ${out}`;
    }
    case 'doctor-self-save-limit':
      return lang === 'ru'
        ? 'Спасти себя нельзя — лимит уже исчерпан.'
        : 'Врятувати себе не можна — ліміт вичерпано.';
    case 'spy-mafia-target':
      return lang === 'ru'
        ? `Мафия выбрала жертвой: ${ev.targetName}`
        : `Мафія обрала жертвою: ${ev.targetName}`;
    case 'spy-don-check':
      return lang === 'ru'
        ? `Дон проверял ${ev.targetName}.`
        : `Дон перевіряв ${ev.targetName}.`;
    case 'cult-recruit':
      return lang === 'ru'
        ? `${ev.targetName} присоединяется к вашему культу.`
        : `${ev.targetName} приєднується до вашого культу.`;
    case 'cult-recruited':
      return lang === 'ru'
        ? 'Вас завербовали в культ. Теперь вы побеждаете вместе с Культистом.'
        : 'Вас завербували в культ. Тепер ви перемагаєте разом із Культистом.';
    case 'witch-control':
      return lang === 'ru'
        ? `Вы навели ${ev.targetName} на цель ${ev.ontoName}.`
        : `Ви навели ${ev.targetName} на ціль ${ev.ontoName}.`;
  }
}
